import type { CSSProperties } from "react";
import { ImageOff } from "lucide-react";

import { cn } from "../lib/cn";

/**
 * Sits behind an image while it downloads: the frame's own tone (or a tiny blurred preview via
 * `style.backgroundImage`) with a light sweep. The image paints over it as it arrives, so nothing
 * is hidden and nothing moves.
 */
export function ImageShimmer({ className, style }: { className?: string; style?: CSSProperties }) {
  return (
    <span
      aria-hidden="true"
      data-image-loading=""
      style={style}
      className={cn(
        "pointer-events-none absolute inset-0 overflow-hidden bg-cover bg-center",
        className,
      )}
    >
      <span className="absolute inset-0 bg-linear-to-r from-transparent via-paper/45 to-transparent motion-safe:animate-shimmer motion-reduce:hidden" />
    </span>
  );
}

/** Shown instead of a broken-image icon when a picture can't be loaded. */
export function ImageFallback({
  label,
  compact = false,
  className,
}: {
  /** What the picture showed (its alt text); read out and, on larger frames, shown. */
  label: string;
  compact?: boolean;
  className?: string;
}) {
  return (
    <span
      role="img"
      aria-label={label || "Picture unavailable"}
      data-image-fallback=""
      className={cn(
        "absolute inset-0 flex flex-col items-center justify-center gap-2 p-3 text-center text-ink-400",
        className,
      )}
    >
      <ImageOff className={compact ? "size-4" : "size-8"} aria-hidden="true" />
      {compact || !label ? null : <span className="line-clamp-2 text-body-s">{label}</span>}
    </span>
  );
}
